/* eslint-disable @typescript-eslint/ban-types */
import { BaseQueryApi, createApi, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import KeyCloakService from '../../security/KeycloakService';
import { setToken } from '../../features/user/userSlice';
import AuthErrorHandler from '../../services/AuthErrorHandler';
import { addError, createApiError } from '../../services/errorHandlingService';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_FIXED_TERM_API_URL,
  prepareHeaders: (headers, { getState }) => {
    // Try to get token from Redux state first, then fallback to Keycloak
    let token = (getState() as RootState).user.token;
    
    // If no token in Redux state, get it from Keycloak
    if (!token && KeyCloakService.IsLoggedIn()) {
      token = KeyCloakService.GetToken();
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

const baseQueryWithReauth = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: {}) => {
  let result = await baseQuery(args, api, extraOptions);
  
  // Handle 401 Unauthorized responses with automatic token refresh
  if (result?.error?.status === 401) {
    // console.log('Received 401 response, attempting token refresh...');
    
    try {
      // Attempt to refresh the token using Keycloak with retry logic
      const refreshed = await KeyCloakService.RefreshTokenWithRetry();
      
      if (refreshed) {
        const newToken = KeyCloakService.GetToken();
        
        if (newToken) {
          // Update Redux state with new token
          api.dispatch(setToken(newToken));
          // console.log('Token refreshed successfully, retrying request...');
          
          // Retry the original request with the new token
          result = await baseQuery(args, api, extraOptions);
        } else {
          console.error('Token refresh succeeded but no token available');
          throw new Error('No token available after refresh');
        }
      } else {
        console.error('Token refresh failed');
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      
      // Handle authentication error gracefully without breaking SSO
      AuthErrorHandler.handleAuthError(error, 'RTK Query');
      
      // Return the original 401 error to the caller without logging out
      return result;
    }
  }
  
  // Handle other authentication-related errors
  if (result?.error?.status === 403) {
    console.warn('Access forbidden (403) - insufficient permissions');
  }

  // Dispatch global error for non-401 errors
  if (result.error && result.error.status !== 401) {
    const errorDetails = createApiError(result.error);
    api.dispatch(addError(errorDetails));
  }
  
  // Log API errors for monitoring (in development)
  if (result?.error && import.meta.env.DEV) {
    console.error('API Error:', {
      status: result.error.status,
      data: result.error.data,
      endpoint: typeof args === 'string' ? args : args.url,
    });
  }

  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    sendTestNotification: builder.mutation<string, string>({
      query: (userId) => ({
        url: `/api/notifications/test/${userId}`,
        method: 'POST',
        responseHandler: (response) => response.text(),
      }),
    }),
  }),
});
